/**
 * careStartSessionService — persists a completed CareStart session to Firestore.
 *
 * Writes session history to users/{uid}/careStartSessions (owner-only; see firestore.rules). This
 * is what the provider-handoff QR references: the share token carries only the session's document
 * id, so the token doc itself stays free of clinical data and the redeem function resolves the
 * results server-side.
 *
 * Authenticated patients only — anonymous CareStart sessions have no account to write to.
 */

import firebase from "firebase/compat/app";
import { db } from "services/firebase";

/**
 * Persist one CareStart session for a signed-in patient.
 *
 * @param {string} patientUid - Firebase Auth uid (must match the caller).
 * @param {object} payload
 * @param {object|null|undefined} payload.triage - { careLevel, diagnosticNeeds, actionGuidance }
 * @param {object|null|undefined} payload.ddxResult - full DDX result
 * @param {string} [payload.finalTranscript]
 * @param {number|string|null} [payload.patientAge]
 * @returns {Promise<string|null>} New session document id, or null if skipped/failed.
 */
export async function persistCareStartSession(patientUid, payload) {
  if (!patientUid || !payload?.triage) return null;

  try {
    const docRef = await db
      .collection("users")
      .doc(patientUid)
      .collection("careStartSessions")
      .add({
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        source: "carestart",
        careLevel: payload.triage.careLevel ?? null,
        diagnosticNeeds: payload.triage.diagnosticNeeds ?? [],
        actionGuidance: payload.triage.actionGuidance ?? null,
        ddxResult: payload.ddxResult ?? null,
        finalTranscript: payload.finalTranscript ?? "",
        patientAge: payload.patientAge ?? null,
      });
    return docRef.id;
  } catch (err) {
    console.warn("[careStartSession] persist failed:", err);
    return null;
  }
}

export default { persistCareStartSession };
